import { useCallback, useRef, useState, type FormEvent, type RefObject } from "react";
import type {
  CopilotChatActivityStage,
  CopilotResponseLength,
  CopilotConversationSummary,
  CopilotInstructionProfile,
  CopilotSavedPrompt,
  CopilotAgent,
  CopilotUploadPolicy,
  KnowledgeScope,
} from "@yazamco/shared-types";
import { useClickOutside } from "../../lib/useClickOutside";
import { Icon } from "../../components/ui/Icon";
import { CopilotLogo } from "../../components/ui/CopilotLogo";
import { MessageList } from "./MessageList";
import { Composer } from "./Composer";
import type { ChatMessage, PendingFile } from "./copilot-chat";
import { pickStarterSuggestions } from "./starter-suggestions";

interface ConversationViewProps {
  active: CopilotConversationSummary | null;
  messages: ChatMessage[];
  busy: boolean;
  activity: { stage: CopilotChatActivityStage; message: string } | null;
  savedPrompts: CopilotSavedPrompt[];
  copiedId: number | null;
  reconnectMessage: string | null;
  profile: Omit<CopilotInstructionProfile, "knowledgeScope"> | null;

  draft: string;
  onDraftChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  onStop: () => void;
  onAsk: (message: string, aimedAt?: KnowledgeScope) => void;
  onCopyAnswer: (message: ChatMessage) => void;
  onEditQuestion: (text: string) => void;
  promptBefore: (messageId: number) => string | null;

  files: PendingFile[];
  onFilesChange: (next: (current: PendingFile[]) => PendingFile[]) => void;
  uploadPolicy: CopilotUploadPolicy;
  filePickerOpen: boolean;
  onFilePickerOpenChange: (open: boolean) => void;
  onReconnectRequired: (message: string) => void;

  scope: KnowledgeScope;
  onScopeChange: (scope: KnowledgeScope) => void;
  agentId: string | null;
  agents: CopilotAgent[];
  activeAgent: CopilotAgent | null;
  onAgentChange: (id: string | null) => void;

  onOpenSettings: () => void;
  onToggleWeb: () => void;
  onResponseLengthChange: (length: CopilotResponseLength) => void;
  /**
   * The canvas's export menu has three items, and the third is not a
   * download: "Copy link to this chat" puts a URL that reopens this
   * conversation on the clipboard. The page owns it because only the page
   * knows the chat's id.
   */
  onExport: (format: "md" | "json") => void;
  onCopyLink: () => void;

  composerRef: RefObject<HTMLFormElement>;
  textareaRef: RefObject<HTMLTextAreaElement>;
  historyRef: RefObject<HTMLDivElement>;
}

/**
 * One conversation: the 64px header that names it, the transcript, the
 * reconnect notice and the composer.
 *
 * Two arrangements, both from the canvas. With no turns yet, the composer is
 * the centre of the page under a short introduction and above the suggestion
 * list; once a chat has turns it sits at the bottom, under a scrolling
 * transcript held to a 740px measure.
 */
export function ConversationView({
  active,
  messages,
  busy,
  activity,
  savedPrompts,
  copiedId,
  reconnectMessage,
  profile,
  draft,
  onDraftChange,
  onSubmit,
  onStop,
  onAsk,
  onCopyAnswer,
  onEditQuestion,
  promptBefore,
  files,
  onFilesChange,
  uploadPolicy,
  filePickerOpen,
  onFilePickerOpenChange,
  onReconnectRequired,
  scope,
  onScopeChange,
  agentId,
  agents,
  activeAgent,
  onAgentChange,
  onOpenSettings,
  onToggleWeb,
  onResponseLengthChange,
  onExport,
  onCopyLink,
  composerRef,
  textareaRef,
  historyRef,
}: ConversationViewProps): JSX.Element {
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  const closeExport = useCallback(() => setExportOpen(false), []);
  useClickOutside(exportRef, exportOpen, closeExport);

  const empty = messages.length === 0;
  /**
   * Three of the static pool, drawn once per mount so the list does not
   * reshuffle under the cursor on every render. "Shuffle" draws again.
   */
  const [suggestions, setSuggestions] = useState(pickStarterSuggestions);
  const shuffleSuggestions = useCallback(
    () => setSuggestions(pickStarterSuggestions()),
    [],
  );

  const composer = (
    <Composer
      draft={draft}
      onDraftChange={onDraftChange}
      onSubmit={onSubmit}
      busy={busy}
      onStop={onStop}
      files={files}
      onFilesChange={onFilesChange}
      uploadPolicy={uploadPolicy}
      filePickerOpen={filePickerOpen}
      onFilePickerOpenChange={onFilePickerOpenChange}
      onReconnectRequired={onReconnectRequired}
      scope={scope}
      onScopeChange={onScopeChange}
      agentId={agentId}
      agents={agents}
      activeAgent={activeAgent}
      onAgentChange={onAgentChange}
      profile={profile}
      onOpenSettings={onOpenSettings}
      onToggleWeb={onToggleWeb}
      onResponseLengthChange={onResponseLengthChange}
      composerRef={composerRef}
      textareaRef={textareaRef}
    />
  );

  return (
    <section
      className={`cp-shell${empty ? " is-empty" : ""}`}
      aria-label="Microsoft 365 Copilot chat"
    >
      <header className="cp-header">
        <div className="cp-header-copy">
          <h1>{activeAgent ? activeAgent.name : active ? active.title : "New chat"}</h1>
          <span>{headerMeta(active, messages.length, profile, activeAgent)}</span>
        </div>
        <div className="cp-header-actions">
          {/* A second agent picker — a bare <select> — stood here beside the
              composer's own. They chose between different things and wrote to
              different state; now there is one record to choose, so there is
              one control for it, and it is the composer's, next to the question
              it applies to. */}
          <div className="cp-export" ref={exportRef}>
            <button
              type="button"
              className={`cp-icon-action${exportOpen ? " is-open" : ""}`}
              title="Export"
              aria-label="Export"
              disabled={messages.length === 0}
              aria-haspopup="menu"
              aria-expanded={exportOpen}
              onClick={() => setExportOpen((current) => !current)}
            >
              <Icon name="export" size={16} />
            </button>
            {exportOpen && (
              <div className="cp-menu cp-export-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  onClick={() => {
                    setExportOpen(false);
                    onExport("md");
                  }}
                >
                  Download as Markdown
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  onClick={() => {
                    setExportOpen(false);
                    onExport("json");
                  }}
                >
                  Download as JSON
                </button>
                {!activeAgent && <button
                  type="button"
                  role="menuitem"
                  className="menu-item"
                  onClick={() => {
                    setExportOpen(false);
                    onCopyLink();
                  }}
                  data-testid="copilot-copy-link"
                >
                  Copy link to this chat
                </button>}
              </div>
            )}
          </div>
          {!activeAgent && <button
            type="button"
            className="cp-icon-action"
            title="Settings"
            aria-label="Settings"
            onClick={onOpenSettings}
          >
            <Icon name="sliders" size={16} />
          </button>}
        </div>
      </header>

      <div className="cp-body">
        {empty ? (
          <div className="cp-measure cp-empty-intro">
            <div className="cp-empty-title">
              <span className="cp-copilot-mark cp-copilot-mark-lg">
                {activeAgent ? <Icon name="brain" size={26} /> : <CopilotLogo size={30} />}
              </span>
              <h2>{activeAgent?.name ?? "Microsoft 365 Copilot"}</h2>
            </div>
            <p>
              {activeAgent
                ? activeAgent.description || "Ask this agent using its saved instructions and knowledge."
                : "Ask about your mail, meetings, chats and files — and the business systems connected to this workspace. Copilot only uses content you can already open."}
            </p>
          </div>
        ) : (
          <MessageList
            messages={messages}
            busy={busy}
            activity={activity}
            scope={scope}
            copiedId={copiedId}
            historyRef={historyRef}
            onAsk={onAsk}
            onCopyAnswer={onCopyAnswer}
            onEditQuestion={onEditQuestion}
            promptBefore={promptBefore}
          />
        )}

        <div className="cp-composer-dock">
          <div className="cp-measure">
            {reconnectMessage && <ReconnectNotice message={reconnectMessage} />}
            {composer}

            {empty && !activeAgent ? (
              <div className="cp-starters">
                <div className="cp-starters-label-row">
                  <span className="cp-starters-label">Start with</span>
                  <button
                    type="button"
                    className="cp-starters-refresh"
                    onClick={shuffleSuggestions}
                    aria-label="Show other suggestions"
                    title="Show other suggestions"
                  >
                    <Icon name="refresh" size={14} />
                    <span>Shuffle</span>
                  </button>
                </div>
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.text}
                    type="button"
                    onClick={() => onDraftChange(suggestion.text)}
                  >
                    <Icon name="arrowRight" size={15} stroke={1.8} />
                    <span>{suggestion.text}</span>
                    <small>{suggestion.tag}</small>
                  </button>
                ))}
                {savedPrompts.length > 0 && (
                  <>
                    <div className="cp-starters-label-row cp-starters-saved-label">
                      <span className="cp-starters-label">Saved prompts</span>
                    </div>
                    {savedPrompts.map((prompt) => (
                      <button
                        key={prompt.id}
                        type="button"
                        title={prompt.prompt}
                        onClick={() => onDraftChange(prompt.prompt)}
                      >
                        <Icon name="arrowRight" size={15} stroke={1.8} />
                        <span>{prompt.name}</span>
                        <small>Saved</small>
                      </button>
                    ))}
                  </>
                )}
              </div>
            ) : !empty ? (
              <p className="cp-disclaimer">
                {activeAgent?.name ?? "Copilot"} can make mistakes. Check important information against its
                sources.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * The line under the chat title. The canvas shows what this chat is and how it
 * is currently aimed; here that is the turn count and whether web grounding is
 * on, which are the two facts the app actually knows about a conversation.
 */
function headerMeta(
  active: CopilotConversationSummary | null,
  messageCount: number,
  profile: Omit<CopilotInstructionProfile, "knowledgeScope"> | null,
  activeAgent: CopilotAgent | null,
): string {
  if (activeAgent) {
    return messageCount === 0
      ? "Uses saved instructions, knowledge and enabled tools"
      : `${messageCount} ${messageCount === 1 ? "message" : "messages"} · Agent knowledge`;
  }
  const parts: string[] = [];
  if (active) {
    parts.push(`${active.turnCount} ${active.turnCount === 1 ? "turn" : "turns"}`);
  } else if (messageCount === 0) {
    parts.push("Nothing asked yet");
  }
  parts.push(profile?.webGrounding ? "Work + web" : "Work only");
  return parts.join(" · ");
}

/**
 * A sign-in that no longer carries the Copilot permissions. Drawn as the
 * canvas's retryable inline error: danger-tinted, a 3px left rule, and the one
 * action that can fix it.
 */
function ReconnectNotice({ message }: { message: string }): JSX.Element {
  return (
    <div className="cp-inline-error" role="alert">
      <div>
        <p className="cp-inline-error-title">Copilot needs to be reconnected</p>
        <p>{message}</p>
        <a className="cp-inline-error-action" href="/api/v1/auth/entra/login?returnTo=%2Fcopilot">
          Sign in again
        </a>
      </div>
    </div>
  );
}
